import { useState, useEffect, useCallback } from 'react';
import { Arquivo, FiltrosArquivo, Paginacao, ErroApi } from '../types/gerArquivos';
import { getLastTenDaysRange } from '../utils/date';
import { GerArquivosService } from '../services/gerArquivosService';

function withDerivedDateFilter(filtros: FiltrosArquivo): FiltrosArquivo {
  return {
    ...filtros,
    dateFilterEnabled: Boolean(filtros.dateFrom || filtros.dateTo),
  };
}

interface UseGerArquivosReturn {
  arquivos: Arquivo[];
  paginacao: Paginacao;
  filtros: FiltrosArquivo;
  carregando: boolean;
  erro: ErroApi | null;
  atualizarFiltros: (novosFiltros: Partial<FiltrosArquivo>) => void;
  alternarFavorito: (arquivoId: string) => Promise<void>;
  recarregarArquivos: () => Promise<void>;
  irParaPagina: (pagina: number) => void;
}

export function useGerArquivos(): UseGerArquivosReturn {
  const [arquivos, setArquivos] = useState<Arquivo[]>([]);
  const [paginacao, setPaginacao] = useState<Paginacao>({
    paginaAtual: 1,
    itensPorPagina: 10,
    totalItens: 0,
    totalPaginas: 0
  });
  const defaultDates = getLastTenDaysRange();

  const [filtros, setFiltros] = useState<FiltrosArquivo>(() => withDerivedDateFilter({
    searchTerm: '',
    dateFrom: defaultDates.from,
    dateTo: defaultDates.to,
    dateFilterEnabled: true,
    showAllUsers: false,
    sort_by: 'created_at',
    sort_dir: 'desc',
  }));
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);

  const buscarArquivos = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    try {
      const response = await GerArquivosService.buscarArquivos(filtros, {
        paginaAtual: paginacao.paginaAtual,
        itensPorPagina: paginacao.itensPorPagina,
      });

      if (response.success) {
        setArquivos(response.data.arquivos);
        setPaginacao(prev => ({
          ...prev,
          ...response.data.paginacao,
          itensPorPagina: prev.itensPorPagina,
        }));
        try {
          (window as { sendToTerminal?: (tag: string, data: unknown) => void }).sendToTerminal?.(
            'GERARQ:LIST_OK',
            { count: response.data.arquivos.length, total: response.data.paginacao.totalItens },
          );
        } catch {}
      } else {
        setErro({
          codigo: 'LIST_ERROR',
          mensagem: response.error || 'Erro ao buscar arquivos',
        });
      }
    } catch (error) {
      setErro({
        codigo: 'API_UNAVAILABLE',
        mensagem: 'Erro de conexão com o servidor',
        detalhes: error instanceof Error ? error.message : 'Erro desconhecido',
      });
      try { (window as { sendToTerminal?: (tag: string, data: unknown) => void }).sendToTerminal?.('GERARQ:LIST_ERROR', { code: 'API_UNAVAILABLE' }); } catch {}
    } finally {
      setCarregando(false);
    }
  }, [filtros, paginacao.paginaAtual, paginacao.itensPorPagina]);

  const alternarFavorito = useCallback(async (arquivoId: string) => {
    try {
      const arquivo = arquivos.find(a => a.id === arquivoId);
      if (!arquivo) return;
      if (arquivo.acoes && !arquivo.acoes.podeFavoritar) return;

      const response = await GerArquivosService.alternarFavorito(arquivoId, !arquivo.favorito);

      if (response.success) {
        setArquivos(prev => prev.map(a =>
          a.id === arquivoId ? { ...a, favorito: !a.favorito } : a
        ));
        try { (window as { sendToTerminal?: (tag: string, data: unknown) => void }).sendToTerminal?.('GERARQ:FAVORITO_OK', { id: arquivoId }); } catch {}
      } else {
        setErro({ codigo: 'VALIDATION_ERROR', mensagem: 'Erro ao atualizar favorito' });
      }
    } catch {
      setErro({ codigo: 'API_UNAVAILABLE', mensagem: 'Erro ao atualizar favorito' });
    }
  }, [arquivos]);

  const atualizarFiltros = useCallback((novosFiltros: Partial<FiltrosArquivo>) => {
    setFiltros(prev => withDerivedDateFilter({ ...prev, ...novosFiltros }));
    setPaginacao(prev => ({ ...prev, paginaAtual: 1 }));
  }, []);

  const recarregarArquivos = useCallback(async () => { await buscarArquivos(); }, [buscarArquivos]);
  const irParaPagina = useCallback((pagina: number) => { setPaginacao(prev => ({ ...prev, paginaAtual: pagina })); }, []);

  useEffect(() => { buscarArquivos(); }, [buscarArquivos]);

  return { arquivos, paginacao, filtros, carregando, erro, atualizarFiltros, alternarFavorito, recarregarArquivos, irParaPagina };
}
